import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import useApiCall from "./useApiCall";

const useBulkValidation = (ep, inscriptions, { fetchInscriptions } = {}) => {
  const apiCall = useApiCall();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleToggleSelectAll = useCallback(
    (visibleInscriptions) => {
      const visibleIds = (visibleInscriptions || inscriptions).map((i) => i.id);
      setSelectedIds((prev) =>
        prev.length === visibleIds.length &&
        visibleIds.every((id) => prev.includes(id))
          ? []
          : visibleIds,
      );
    },
    [inscriptions],
  );

  const handleSelectAll = useCallback(
    (visibleInscriptions) => {
      setSelectedIds((visibleInscriptions || inscriptions).map((i) => i.id));
    },
    [inscriptions],
  );

  const handleValidateSelected = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.warning("Veuillez sélectionner au moins un allocataire");
      return;
    }
    setOverwriteExisting(false);
    setShowBulkModal(true);
  }, [selectedIds.length]);

  const handleConfirmBulkValidate = useCallback(() => {
    const selectedInscriptions = inscriptions.filter((i) =>
      selectedIds.includes(i.id),
    );

    const hasModifiedDecisions = selectedInscriptions.some(
      (i) => i.decisionId && i.decisionId !== i.selectedProposal,
    );

    let idsToSend;
    if (overwriteExisting || !hasModifiedDecisions) {
      idsToSend = selectedIds;
    } else {
      idsToSend = selectedInscriptions
        .filter((i) => !i.decisionId || i.decisionId === i.selectedProposal)
        .map((i) => i.id);
    }

    if (idsToSend.length === 0) {
      toast.warning(
        "Toutes les inscriptions sélectionnées ont déjà une décision modifiée. Cochez l'option pour les écraser.",
      );
      return;
    }

    setIsBulkSaving(true);
    apiCall(
      "patch",
      `eps/${ep.id}/valider-propositions-ru`,
      { ids: idsToSend },
      () => {
        toast.success(
          `${idsToSend.length} proposition(s) validée(s) avec succès`,
        );
        setIsBulkSaving(false);
        setShowBulkModal(false);
        setSelectedIds([]);
        if (fetchInscriptions) fetchInscriptions();
      },
      () => {
        setIsBulkSaving(false);
      },
    );
  }, [
    apiCall,
    ep?.id,
    fetchInscriptions,
    inscriptions,
    overwriteExisting,
    selectedIds,
  ]);

  const pendingDecisionCount = inscriptions.filter((i) => !i.decisionId).length;
  const canClose = pendingDecisionCount === 0 && inscriptions.length > 0;

  return {
    selectedIds,
    setSelectedIds,
    clearSelection,
    showBulkModal,
    setShowBulkModal,
    overwriteExisting,
    setOverwriteExisting,
    isBulkSaving,
    pendingDecisionCount,
    canClose,
    handleToggleSelect,
    handleToggleSelectAll,
    handleSelectAll,
    handleValidateSelected,
    handleConfirmBulkValidate,
  };
};

export default useBulkValidation;
